import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Typography, Button, Space, Card, Tag, Popconfirm, message, Tooltip, Modal, Input, Select,
} from 'antd';
import {
  DownloadOutlined, CheckCircleOutlined, MailOutlined, DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from '../../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

const STATUS_COLORS = { generated: 'blue', emailed: 'orange', paid: 'green' };
const STATUS_LABELS = { generated: 'Generated', emailed: 'Emailed', paid: 'Paid' };

const fmtMoney = (v) => `£${Number(v || 0).toFixed(2)}`;

const OpcInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState(null);
  const [paidNotes, setPaidNotes] = useState('');
  const [markingPaidId, setMarkingPaidId] = useState(null);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const res = await apiService.opcInvoices.list(params);
      setInvoices(res.data || []);
    } catch {
      message.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleDownload = async (id) => {
    try {
      const res = await apiService.opcInvoices.download(id);
      window.open(res.data.url, '_blank');
    } catch {
      message.error('Failed to get download link');
    }
  };

  const handleMarkEmailed = async (id) => {
    try {
      await apiService.opcInvoices.markEmailed(id);
      message.success('Marked as emailed');
      fetchInvoices();
    } catch {
      message.error('Failed to update');
    }
  };

  const handleMarkPaid = async () => {
    try {
      await apiService.opcInvoices.markPaid(markingPaidId, paidNotes);
      message.success('Marked as paid');
      setMarkingPaidId(null);
      setPaidNotes('');
      fetchInvoices();
    } catch {
      message.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiService.opcInvoices.remove(id);
      message.success('Deleted');
      fetchInvoices();
    } catch {
      message.error('Failed to delete');
    }
  };

  const columns = [
    {
      title: 'Client',
      dataIndex: 'client',
      key: 'client',
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: 'Period',
      key: 'period',
      render: (_, r) =>
        `${dayjs(r.startDate).format('DD MMM YY')} – ${dayjs(r.endDate).format('DD MMM YY')}`,
    },
    {
      title: 'Runs',
      dataIndex: 'runCount',
      key: 'runCount',
      align: 'center',
      width: 70,
    },
    {
      title: 'Total',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      align: 'right',
      width: 110,
      render: fmtMoney,
    },
    {
      title: 'Generated',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      render: (v) => dayjs(v).format('DD MMM YYYY'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s, r) => (
        <Tooltip title={
          s === 'emailed' ? `Emailed ${dayjs(r.emailedAt).format('DD MMM')}` :
          s === 'paid'    ? `Paid ${dayjs(r.paidAt).format('DD MMM')}` : ''
        }>
          <Tag color={STATUS_COLORS[s]}>{STATUS_LABELS[s]}</Tag>
        </Tooltip>
      ),
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      width: 180,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Download">
            <Button size="small" type="text" icon={<DownloadOutlined />} onClick={() => handleDownload(record._id)} />
          </Tooltip>
          {record.status === 'generated' && (
            <Tooltip title="Mark as emailed">
              <Button size="small" type="text" icon={<MailOutlined />} onClick={() => handleMarkEmailed(record._id)} />
            </Tooltip>
          )}
          {record.status !== 'paid' && (
            <Tooltip title="Mark as paid">
              <Button
                size="small"
                type="text"
                icon={<CheckCircleOutlined style={{ color: '#3f8600' }} />}
                onClick={() => { setMarkingPaidId(record._id); setPaidNotes(''); }}
              />
            </Tooltip>
          )}
          <Popconfirm title="Delete this invoice?" onConfirm={() => handleDelete(record._id)} okText="Yes" cancelText="No" okButtonProps={{ danger: true }}>
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Invoices</Title>
        <Select
          placeholder="All statuses"
          allowClear
          value={filterStatus}
          onChange={setFilterStatus}
          style={{ width: 150 }}
        >
          <Option value="generated">Generated</Option>
          <Option value="emailed">Emailed</Option>
          <Option value="paid">Paid</Option>
        </Select>
      </div>

      <Card size="small">
        <Table
          dataSource={invoices}
          columns={columns}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 25, hideOnSinglePage: true }}
          size="small"
          locale={{ emptyText: 'No invoices yet — generate one from the Breakdown page' }}
        />
      </Card>

      <Modal
        open={!!markingPaidId}
        title="Mark as paid"
        onCancel={() => setMarkingPaidId(null)}
        onOk={handleMarkPaid}
        okText="Confirm"
      >
        <Input.TextArea
          placeholder="Notes (optional)"
          value={paidNotes}
          onChange={e => setPaidNotes(e.target.value)}
          rows={3}
        />
      </Modal>
    </div>
  );
};

export default OpcInvoices;
